import { Button, Input, Select, useToast } from "@design";
import { useMutation } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addTeamMemberMutation,
  errorMessage,
  removeTeamMemberMutation,
  type TeamMemberResponse,
  type TeamResponse,
  type UserSearchResult,
  updateTeamMemberMutation,
} from "@/api";
import { UserSearchCombobox } from "@/components/build/UserSearchCombobox";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { TeamMemberRow } from "./TeamMemberRow";
import { isOnlyAdmin, TEAM_ROLES, type TeamRole } from "./teamRoles";
import { useRefreshTeams } from "./useRefreshTeams";

const FILTER_FROM = 10;

/** A team's members; team admins and platform admins add people, change roles and remove them (MHS-1082). */
export function TeamMembersSection({
  team,
  canManage,
  readOnlyNote,
  myUuid,
}: Readonly<{ team: TeamResponse; canManage: boolean; readOnlyNote?: string; myUuid?: string }>) {
  const { t } = useTranslation("teams");
  const toast = useToast();
  const refreshTeams = useRefreshTeams();
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<UserSearchResult | null>(null);
  const [pickerKey, setPickerKey] = useState(0);
  const [role, setRole] = useState<TeamRole>("member");
  const [removing, setRemoving] = useState<TeamMemberResponse | null>(null);
  const path = { team_uuid: team.uuid };
  const members = team.members ?? [];
  const failed = (error: unknown) => toast.error(errorMessage(error, t("toast.failed")));

  const add = useMutation({
    ...addTeamMemberMutation(),
    onSuccess: async (_, vars) => {
      toast.success(t("toast.added", { name: picked?.name ?? vars.body.email }));
      setPicked(null);
      setPickerKey((k) => k + 1);
      await refreshTeams();
    },
    onError: failed,
  });
  const changeRole = useMutation({
    ...updateTeamMemberMutation(),
    onSuccess: async (_, vars) => {
      const name = members.find((m) => m.userUuid === vars.path.user_uuid)?.name ?? "";
      toast.success(t("toast.roleChanged", { name, role: t(`roles.${vars.body.role as TeamRole}`) }));
      await refreshTeams();
    },
    onError: failed,
  });
  const remove = useMutation({
    ...removeTeamMemberMutation(),
    onSuccess: async () => {
      toast.success(t("toast.removed", { name: removing?.name ?? "" }));
      setRemoving(null);
      await refreshTeams();
    },
    onError: failed,
  });

  const needle = query.trim().toLowerCase();
  const shown = needle
    ? members.filter((m) => m.name.toLowerCase().includes(needle) || m.email.toLowerCase().includes(needle))
    : members;
  const editable = canManage;

  return (
    <PageSection title={t("detail.members")} description={t("detail.membersBody")}>
      {team.isEveryone && (
        <Text variant="body-sm" tone="secondary">
          {t("everyoneNote")}
        </Text>
      )}
      {members.length > FILTER_FROM && (
        <Input
          size="md"
          aria-label={t("detail.filter")}
          placeholder={t("detail.filter")}
          leadingIcon={<Search size={16} />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}
      <ul aria-label={t("detail.membersLabel")} className="flex flex-col divide-y divide-border-subtle">
        {shown.map((m) => (
          <TeamMemberRow
            key={m.userUuid}
            member={m}
            isYou={m.userUuid === myUuid}
            editable={editable}
            removable={!team.isEveryone && !isOnlyAdmin(m, members)}
            onlyAdmin={isOnlyAdmin(m, members)}
            onRole={(next) =>
              changeRole.mutate({ path: { ...path, user_uuid: m.userUuid }, body: { role: next }, throwOnError: true })
            }
            onRemove={() => setRemoving(m)}
          />
        ))}
      </ul>
      {needle && shown.length === 0 && (
        <Text variant="body-sm" tone="tertiary">
          {t("detail.noMatches", { query })}
        </Text>
      )}
      {editable && !team.isEveryone && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-56 flex-1">
            <UserSearchCombobox key={pickerKey} excludeUuids={members.map((m) => m.userUuid)} onSelect={setPicked} />
          </div>
          <div className="w-36">
            <Select
              size="md"
              aria-label={t("detail.addRole")}
              options={TEAM_ROLES.map((r) => ({ value: r, label: t(`roles.${r}`) }))}
              value={role}
              onChange={(v) => setRole(v as TeamRole)}
            />
          </div>
          <Button
            variant="secondary"
            size="md"
            loading={add.isPending}
            disabled={!picked}
            onClick={() => picked && add.mutate({ path, body: { email: picked.email, role }, throwOnError: true })}
          >
            {t("detail.add")}
          </Button>
        </div>
      )}
      {editable && (
        <Text variant="caption" tone="tertiary">
          {t("roleHelp")}
        </Text>
      )}
      {!editable && readOnlyNote && (
        <Text variant="caption" tone="tertiary">
          {readOnlyNote}
        </Text>
      )}
      <ConfirmDialog
        open={removing !== null}
        title={t("confirmRemove.title", { name: removing?.name ?? "" })}
        body={t("confirmRemove.body", { team: team.name })}
        confirmLabel={t("confirmRemove.confirm")}
        loading={remove.isPending}
        onConfirm={() =>
          removing && remove.mutate({ path: { ...path, user_uuid: removing.userUuid }, throwOnError: true })
        }
        onClose={() => setRemoving(null)}
      />
    </PageSection>
  );
}
